import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { ShieldCheck, PackageCheck, CalendarDays, CheckCircle2, MapPin, XCircle, Wrench, Star, Paperclip } from "lucide-react";

const calcD3 = () => {
  const d = new Date(Date.now() + 3 * 86400000);
  while (d.getUTCDay() === 0) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function PortalPedido() {
  const { token } = useParams();
  const [fase, setFase] = useState("otp"); // otp, codigo, pedido
  const [sessao, setSessao] = useState("");
  const [codigo, setCodigo] = useState("");
  const [mascara, setMascara] = useState("");
  const [pedido, setPedido] = useState(null);
  const [end, setEnd] = useState({});
  const [datas, setDatas] = useState([calcD3(), "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [descAssist, setDescAssist] = useState("");
  const [mostraAssist, setMostraAssist] = useState(false);
  const [estrelas, setEstrelas] = useState(0);
  const [comentario, setComentario] = useState("");
  const [anexos, setAnexos] = useState({ fotos: 0, videos: 0 });

  const solicitarOtp = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/${token}/otp/solicitar`);
      setMascara(r.data.enviado_para);
      setFase("codigo");
      toast.success("Código enviado por WhatsApp!");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const validarOtp = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/${token}/otp/validar`, { codigo });
      setSessao(r.data.sessao);
      await carregar(r.data.sessao);
      setFase("pedido");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const carregar = async (s = sessao) => {
    const r = await axios.get(`${API}/portal/${token}`, { headers: { "X-Portal-Sessao": s } });
    setPedido(r.data);
    setEnd({ cep: r.data.cliente.cep || "", endereco: r.data.cliente.endereco || "", referencia: r.data.cliente.referencia || "" });
  };

  const salvarEndereco = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/endereco`, end, { headers: { "X-Portal-Sessao": sessao } });
      toast.success("Endereço atualizado!");
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const produtoEntregue = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/produto-entregue`, {}, { headers: { "X-Portal-Sessao": sessao } });
      toast.success("Entrega confirmada! Agora escolha as 5 datas.");
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const setData = (i, v) => {
    if (v && new Date(v + "T12:00:00").getDay() === 0) { toast.error("Domingos não estão disponíveis."); return; }
    if (v && datas.some((d, j) => j !== i && d === v)) { toast.error("Data repetida."); return; }
    setDatas(ds => ds.map((d, j) => (j === i ? v : d)));
  };

  const agendar = async () => {
    if (datas.filter(Boolean).length < 5) { toast.error("Informe as 5 datas"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/${token}/agendar`, { datas }, { headers: { "X-Portal-Sessao": sessao } });
      toast.success(r.data.mensagem);
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const simularCancelamento = async () => {
    try {
      const r = await axios.get(`${API}/portal/${token}/cancelamento`, { headers: { "X-Portal-Sessao": sessao } });
      setDemo(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelar = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/cancelar`, { motivo }, { headers: { "X-Portal-Sessao": sessao } });
      toast.success("Pedido cancelado. Demonstrativo enviado por WhatsApp.");
      setDemo(null);
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const [fotosAssist, setFotosAssist] = useState([]);
  const [videoAssist, setVideoAssist] = useState(null);

  const validarVideo = (f) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration <= 10.5); };
    v.onerror = () => resolve(true);
    v.src = URL.createObjectURL(f);
  });

  const escolherVideo = async (f) => {
    if (!f) return;
    const ok = await validarVideo(f);
    if (!ok) { toast.error("O vídeo deve ter no máximo 10 segundos"); return; }
    setVideoAssist(f);
  };

  const solicitarAssistencia = async () => {
    if (fotosAssist.length < 1) { toast.error("Anexe pelo menos 1 foto do problema (até 5)"); return; }
    if (!videoAssist) { toast.error("Anexe 1 vídeo de até 10 segundos mostrando o problema"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/${token}/assistencia`, { descricao: descAssist }, { headers: { "X-Portal-Sessao": sessao } });
      const aid = r.data.id;
      for (const f of [...fotosAssist.slice(0, 5), videoAssist]) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/portal/${token}/assistencia/${aid}/anexo`, fd, { headers: { "X-Portal-Sessao": sessao } });
      }
      toast.success(r.data.mensagem);
      setMostraAssist(false);
      setDescAssist("");
      setFotosAssist([]);
      setVideoAssist(null);
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const avaliar = async () => {
    if (!estrelas) { toast.error("Escolha de 1 a 5 estrelas"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/avaliar`, { estrelas, comentario }, { headers: { "X-Portal-Sessao": sessao } });
      toast.success("Obrigado pela avaliação! 💙");
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const anexar = async (assistId, f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/portal/${token}/assistencia/${assistId}/anexo`, fd, { headers: { "X-Portal-Sessao": sessao } });
      setAnexos({ fotos: r.data.fotos, videos: r.data.videos });
      toast.success(`Anexo enviado (${r.data.fotos}/5 fotos · ${r.data.videos}/1 vídeo)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (fase === "otp") return (
    <Tela>
      <div className="text-center" data-testid="portal-otp">
        <ShieldCheck size={40} className="mx-auto text-[#FF6700] mb-4" />
        <h1 className="font-bold text-2xl">Portal do Pedido</h1>
        <p className="text-sm text-slate-400 mt-2">Para sua segurança, enviaremos um código de acesso ao WhatsApp cadastrado no pedido.</p>
        <Button onClick={solicitarOtp} disabled={busy}
          className="mt-6 w-full h-12 bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold" data-testid="portal-solicitar-otp">
          {busy ? "Enviando…" : "Receber código por WhatsApp"}
        </Button>
        <button onClick={() => setFase("codigo")} className="mt-3 text-xs text-slate-400 hover:text-white underline" data-testid="portal-ja-tenho-codigo">
          Já tenho um código
        </button>
      </div>
    </Tela>
  );

  if (fase === "codigo") return (
    <Tela>
      <div className="text-center" data-testid="portal-codigo">
        <h1 className="font-bold text-xl">Digite o código</h1>
        <p className="text-sm text-slate-400 mt-2">Enviado para {mascara}. Válido por 10 minutos.</p>
        <Input value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000" maxLength={6} inputMode="numeric"
          className="mt-5 text-center text-2xl tracking-[0.4em] h-14 bg-white text-slate-900" data-testid="portal-otp-input" />
        <Button onClick={validarOtp} disabled={busy || codigo.length !== 6}
          className="mt-4 w-full h-12 bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold" data-testid="portal-validar-otp">
          {busy ? "Validando…" : "Acessar meu pedido"}
        </Button>
        <button onClick={solicitarOtp} className="mt-3 text-xs text-slate-400 hover:text-white underline">Reenviar código</button>
      </div>
    </Tela>
  );

  if (!pedido) return <Tela><p className="text-center text-slate-400">Carregando…</p></Tela>;

  return (
    <Tela larga>
      <div data-testid="portal-pedido">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="font-bold text-xl">Pedido {pedido.protocolo}</h1>
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-white/10" data-testid="portal-status">
            {pedido.status.replace(/_/g, " ")}
          </span>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4 text-sm space-y-1" data-testid="portal-dados">
          <div><span className="text-slate-400">Cliente:</span> {pedido.cliente.nome}</div>
          {pedido.itens.map((i, x) => <div key={x}>• {i.quantidade}x {i.produto}</div>)}
          <div className="pt-2 border-t border-white/10 flex justify-between">
            <span className="text-slate-400">Valor da montagem (pago)</span>
            <strong className="text-[#FF6700]">R$ {Number(pedido.valor_montagem_total).toFixed(2).replace(".", ",")}</strong>
          </div>
          <p className="text-[10px] text-slate-500">Nome, produtos e valor não podem ser alterados após o pagamento.</p>
        </div>

        {pedido.status === "PEDIDO_ABERTO" && !pedido.endereco_bloqueado && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4" data-testid="portal-endereco">
            <div className="flex items-center gap-2 font-semibold text-sm mb-3"><MapPin size={15} className="text-[#FF6700]" /> Endereço do serviço (confira e corrija se necessário)</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input value={end.cep} onChange={e => setEnd({ ...end, cep: e.target.value })} placeholder="CEP" className="bg-white text-slate-900" data-testid="portal-cep" />
              <Input value={end.endereco} onChange={e => setEnd({ ...end, endereco: e.target.value })} placeholder="Endereço e número" className="bg-white text-slate-900 sm:col-span-2" data-testid="portal-rua" />
              <Input value={end.referencia} onChange={e => setEnd({ ...end, referencia: e.target.value })} placeholder="Ponto de referência" className="bg-white text-slate-900 sm:col-span-3" data-testid="portal-ref" />
            </div>
            <Button onClick={salvarEndereco} disabled={busy} variant="outline"
              className="mt-3 border-white/20 text-white bg-transparent hover:bg-white/10 text-xs" data-testid="portal-salvar-endereco">
              Salvar endereço
            </Button>
          </div>
        )}

        {pedido.status === "PEDIDO_ABERTO" && !pedido.produto_entregue && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4 text-center" data-testid="portal-entrega">
            <PackageCheck size={28} className="mx-auto text-[#FF6700] mb-2" />
            <p className="text-sm text-slate-300">O produto já foi entregue no endereço? A montagem só é liberada após a entrega.</p>
            <Button onClick={produtoEntregue} disabled={busy}
              className="mt-3 bg-[#126BFF] hover:bg-[#0f5ad9] text-white font-bold" data-testid="portal-produto-entregue-btn">
              Sim, o produto foi entregue
            </Button>
          </div>
        )}

        {pedido.status === "PEDIDO_ABERTO" && pedido.produto_entregue && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4" data-testid="portal-agendar">
            <div className="flex items-center gap-2 font-semibold text-sm mb-1"><CalendarDays size={15} className="text-[#FF6700]" /> Escolha 5 datas para a montagem</div>
            <p className="text-[11px] text-slate-400 mb-3">
              A partir de {pedido.d3.split("-").reverse().join("/")} (1ª data obrigatória, já preenchida), dentro do prazo do pedido, exceto domingos.
            </p>
            <div className="space-y-2">
              {datas.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input type="date" min={pedido.d3} max={pedido.limite_90d || undefined} value={d}
                    onChange={e => setData(i, e.target.value)} disabled={i === 0}
                    className="bg-white text-slate-900 flex-1" data-testid={`portal-data-${i}`} />
                  {i === 0 && <span className="text-[10px] font-bold uppercase text-slate-400 whitespace-nowrap">1ª data · obrigatória</span>}
                </div>
              ))}
            </div>
            <Button onClick={agendar} disabled={busy}
              className="mt-4 w-full h-12 bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold" data-testid="portal-agendar-btn">
              {busy ? "Registrando…" : "Confirmar datas e liberar montagem"}
            </Button>
          </div>
        )}

        {pedido.status === "AGENDAMENTO_CRIADO" && (
          <div className="bg-white/5 border border-emerald-500/30 rounded-xl p-5 mt-4 text-center" data-testid="portal-agendado">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
            <p className="font-semibold">Datas registradas! {pedido.servico?.pim_code && `OS ${pedido.servico.pim_code}`}</p>
            <p className="text-sm text-slate-400 mt-1">
              {pedido.servico?.data_confirmada
                ? `Montagem agendada para ${pedido.servico.data_confirmada.split("-").reverse().join("/")}.`
                : "Um montador da sua região confirmará uma das suas datas e você será avisado por WhatsApp."}
            </p>
            {pedido.is_demo && (
              <button
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/demo/portal/${token}/simular-montador`);
                    sessionStorage.setItem("pim_token", r.data.token);
                    sessionStorage.setItem("pim_role", r.data.role);
                    window.location.href = `/montador/servico/${r.data.servico_id}`;
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Erro ao abrir o modo montador");
                  }
                }}
                className="mt-4 w-full rounded-xl bg-[#FF6700] text-white py-3 font-extrabold text-sm"
                data-testid="portal-simular-montador">
                👷 Simular agora como Montador
              </button>
            )}
          </div>
        )}

        {["MONTAGEM_CONCLUIDA", "ENCERRADO"].includes(pedido.status) && (
          <div className="bg-white/5 border border-emerald-500/30 rounded-xl p-5 mt-4" data-testid="portal-concluido">
            <div className="text-center">
              <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
              <p className="font-semibold">{pedido.status === "ENCERRADO" ? "Pedido encerrado — obrigado! 💙" : "Montagem concluída! 🎉"}</p>
              <p className="text-sm text-slate-400 mt-1">
                {pedido.garantia_restante > 0
                  ? `Garantia do serviço: restam ${pedido.garantia_restante} dia(s).`
                  : "Prazo de garantia de 30 dias encerrado."}
              </p>
            </div>
            {pedido.assistencias?.length > 0 && (
              <div className="mt-4 space-y-1.5" data-testid="portal-assistencias">
                {pedido.assistencias.map((a, i) => {
                  const aberta = !["finalizado", "cancelado"].includes(a.status);
                  return (
                    <div key={i} className="text-xs bg-white/5 rounded-lg px-3 py-2">
                      <div className="flex justify-between">
                        <span><Wrench size={11} className="inline mr-1 text-[#FF6700]" /> {a.pim_code}</span>
                        <span className="uppercase font-bold text-slate-400">{(a.status || "").replace(/_/g, " ")}</span>
                      </div>
                      {aberta && (
                        <label className="mt-2 inline-flex items-center gap-1.5 cursor-pointer text-[#FF6700] hover:underline" data-testid={`portal-anexar-${i}`}>
                          <Paperclip size={11} /> Anexar foto/vídeo (até 5 fotos + 1 vídeo)
                          <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden"
                            onChange={e => anexar(a.id, e.target.files?.[0])} data-testid={`portal-anexo-input-${i}`} />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!pedido.avaliacao ? (
              <div className="mt-4 border-t border-white/10 pt-4" data-testid="portal-avaliacao">
                <p className="text-sm font-semibold text-center mb-2">Como foi a montagem? Avalie o serviço:</p>
                <div className="flex justify-center gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setEstrelas(n)} data-testid={`portal-estrela-${n}`}>
                      <Star size={30} className={n <= estrelas ? "text-[#FF6700] fill-[#FF6700]" : "text-slate-600"} />
                    </button>
                  ))}
                </div>
                <textarea value={comentario} onChange={e => setComentario(e.target.value)}
                  placeholder="Deixe um comentário (opcional)" rows={2}
                  className="mt-3 w-full rounded-lg bg-white text-slate-900 text-sm p-3" data-testid="portal-avaliacao-comentario" />
                <Button onClick={avaliar} disabled={busy || !estrelas}
                  className="mt-2 w-full bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold" data-testid="portal-enviar-avaliacao">
                  {busy ? "Enviando…" : "Enviar avaliação"}
                </Button>
              </div>
            ) : (
              <div className="mt-4 border-t border-white/10 pt-3 text-center" data-testid="portal-avaliado">
                <div className="flex justify-center gap-0.5">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star key={n} size={20} className={n <= pedido.avaliacao.estrelas ? "text-[#FF6700] fill-[#FF6700]" : "text-slate-600"} />
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">Avaliação enviada. Obrigado! 💙</p>
              </div>
            )}
            {pedido.garantia_restante >= 0 && !mostraAssist &&
              !pedido.assistencias?.some(a => !["finalizado", "cancelado"].includes(a.status)) && (
              <Button onClick={() => setMostraAssist(true)} variant="outline"
                className="mt-4 w-full border-white/20 text-white bg-transparent hover:bg-white/10"
                data-testid="portal-abrir-assistencia">
                <Wrench size={15} className="mr-1.5" /> Solicitar assistência técnica
              </Button>
            )}
            {mostraAssist && (
              <div className="mt-4" data-testid="portal-form-assistencia">
                <textarea value={descAssist} onChange={e => setDescAssist(e.target.value)}
                  placeholder="Descreva o problema encontrado na montagem (mín. 10 caracteres)…"
                  rows={3}
                  className="w-full rounded-lg bg-white text-slate-900 text-sm p-3"
                  data-testid="portal-assistencia-desc" />
                <div className="mt-2 space-y-2 text-xs">
                  <label className="flex items-center gap-2 border border-white/20 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/5">
                    📷 Fotos do problema (obrigatório · {fotosAssist.length}/5)
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={e => setFotosAssist(Array.from(e.target.files || []).slice(0, 5))}
                      data-testid="portal-assist-fotos" />
                  </label>
                  <label className="flex items-center gap-2 border border-white/20 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/5">
                    🎥 Vídeo de até 10s (obrigatório · {videoAssist ? "1/1 ✓" : "0/1"})
                    <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
                      onChange={e => escolherVideo(e.target.files?.[0])}
                      data-testid="portal-assist-video" />
                  </label>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button onClick={solicitarAssistencia}
                    disabled={busy || descAssist.trim().length < 10 || fotosAssist.length < 1 || !videoAssist}
                    className="flex-1 bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold" data-testid="portal-enviar-assistencia">
                    {busy ? "Enviando…" : "Enviar solicitação"}
                  </Button>
                  <Button onClick={() => setMostraAssist(false)} variant="outline"
                    className="border-white/20 text-white bg-transparent hover:bg-white/10">Voltar</Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Resposta inicial em até 72 horas. A assistência usa o mesmo protocolo {pedido.protocolo}.</p>
              </div>
            )}
          </div>
        )}

        {(pedido.status === "CANCELADO" || pedido.status === "REEMBOLSADO") && (
          <div className="bg-white/5 border border-red-500/30 rounded-xl p-5 mt-4" data-testid="portal-cancelado">
            <div className="text-center">
              <XCircle size={30} className="mx-auto text-red-400 mb-2" />
              <p className="font-semibold">{pedido.status === "REEMBOLSADO" ? "Pedido cancelado — reembolso processado 💰" : "Pedido cancelado — reembolso em processamento"}</p>
            </div>
            {pedido.reembolso_demonstrativo && (
              <div className="mt-4 text-sm space-y-1 bg-white/5 rounded-lg p-3" data-testid="portal-demonstrativo">
                <Linha l="Valor pago" v={pedido.reembolso_demonstrativo.valor_bruto} />
                {pedido.reembolso_demonstrativo.dentro_arrependimento ? (
                  <div className="text-emerald-400 text-xs">Arrependimento no prazo legal (7 dias) — reembolso integral</div>
                ) : (
                  <>
                    {pedido.reembolso_demonstrativo.tarifa_gateway > 0 && <Linha l="(-) Tarifa do meio de pagamento" v={pedido.reembolso_demonstrativo.tarifa_gateway} neg />}
                    <Linha l="(-) Retenção operacional 20%" v={pedido.reembolso_demonstrativo.retencao_20} neg />
                    {pedido.reembolso_demonstrativo.deducao_ausencias > 0 && <Linha l="(-) Visitas improdutivas" v={pedido.reembolso_demonstrativo.deducao_ausencias} neg />}
                  </>
                )}
                <div className="border-t border-white/10 pt-1 flex justify-between font-bold">
                  <span>Reembolso</span>
                  <span className="text-[#FF6700]">R$ {Number(pedido.reembolso_demonstrativo.valor_reembolso).toFixed(2).replace(".", ",")}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {pedido.cancelavel && !demo && (
          <button onClick={simularCancelamento}
            className="mt-5 w-full text-center text-xs text-slate-500 hover:text-red-400 underline"
            data-testid="portal-quero-cancelar">
            Preciso cancelar este pedido
          </button>
        )}

        {demo && (
          <div className="bg-white/5 border border-red-500/30 rounded-xl p-4 mt-4" data-testid="portal-cancelamento">
            <p className="font-semibold text-sm mb-2 flex items-center gap-2"><XCircle size={15} className="text-red-400" /> Cancelamento — demonstrativo do reembolso</p>
            <div className="text-sm space-y-1 bg-white/5 rounded-lg p-3">
              <Linha l="Valor pago" v={demo.valor_bruto} />
              {demo.dentro_arrependimento ? (
                <div className="text-emerald-400 text-xs">Você está no prazo legal de arrependimento (7 dias) — reembolso integral ✅</div>
              ) : (
                <>
                  {demo.tarifa_gateway > 0 && <Linha l="(-) Tarifa do meio de pagamento" v={demo.tarifa_gateway} neg />}
                  <Linha l="(-) Retenção operacional 20%" v={demo.retencao_20} neg />
                  {demo.deducao_ausencias > 0 && <Linha l="(-) Visitas improdutivas" v={demo.deducao_ausencias} neg />}
                </>
              )}
              <div className="border-t border-white/10 pt-1 flex justify-between font-bold">
                <span>Você receberá</span>
                <span className="text-[#FF6700]" data-testid="portal-valor-reembolso">R$ {Number(demo.valor_reembolso).toFixed(2).replace(".", ",")}</span>
              </div>
            </div>
            <Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo (opcional)"
              className="mt-3 bg-white text-slate-900" data-testid="portal-motivo-cancelamento" />
            <div className="flex gap-2 mt-3">
              <Button onClick={cancelar} disabled={busy}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="portal-confirmar-cancelamento">
                {busy ? "Cancelando…" : "Confirmar cancelamento"}
              </Button>
              <Button onClick={() => setDemo(null)} variant="outline"
                className="border-white/20 text-white bg-transparent hover:bg-white/10"
                data-testid="portal-manter-pedido">Manter pedido</Button>
            </div>
          </div>
        )}
      </div>
    </Tela>
  );
}

function Linha({ l, v, neg }) {
  return (
    <div className="flex justify-between text-xs">
      <span className={neg ? "text-red-300" : "text-slate-400"}>{l}</span>
      <span className={neg ? "text-red-300" : ""}>R$ {Number(v).toFixed(2).replace(".", ",")}</span>
    </div>
  );
}

function Tela({ children, larga }) {
  return (
    <div className="min-h-screen bg-[#0B1020] text-white flex items-center justify-center p-4">
      <div className={`w-full ${larga ? "max-w-xl" : "max-w-sm"} py-8`}>
        <div className="flex items-center gap-2 mb-8 justify-center">
          <span className="font-extrabold text-lg">Monta<span className="text-[#FF6700]">Já</span></span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Portal do Pedido</span>
        </div>
        {children}
      </div>
    </div>
  );
}
